import { z } from 'zod'

/**
 * Who sees a Live Room someone opened themselves, from narrow to wide: `private` only by its link
 * or QR, `friends` for the creator's friends and friends of friends, `request` listed for everyone
 * but only accepted people get in, `public` for everyone.
 */
export const userRoomVisibilityValues = ['private', 'friends', 'request', 'public'] as const
export type UserRoomVisibility = (typeof userRoomVisibilityValues)[number]

/** What kind of evening a Live Room is; organizer events are mapped onto the same values. */
export const liveRoomCategoryValues = [
  'party',
  'networking',
  'sport',
  'culture',
  'food',
  'study',
  'family',
  'other',
] as const
export type LiveRoomCategory = (typeof liveRoomCategoryValues)[number]

/** How long a user room runs when it is opened; 24 is "Ganzer Tag". */
export const userRoomHoursValues = [4, 6, 8, 12, 24] as const
export type UserRoomHours = (typeof userRoomHoursValues)[number]

/** Hours one extension adds; a room never lives longer than `USER_ROOM_MAX_HOURS` in total. */
export const USER_ROOM_EXTEND_HOURS = 4
export const USER_ROOM_MAX_HOURS = 48

const userRoomHoursSchema = z.union([
  z.literal(4),
  z.literal(6),
  z.literal(8),
  z.literal(12),
  z.literal(24),
])

/** Where a user room is: a Pulsify place, or a point with an optional label. */
export const userRoomWhereSchema = z.union([
  z.object({ listingId: z.string().uuid() }),
  z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    label: z.string().trim().min(1).max(120).optional(),
  }),
])
export type UserRoomWhere = z.infer<typeof userRoomWhereSchema>

export const userRoomCreateSchema = z.object({
  name: z.string().trim().min(1).max(60),
  visibility: z.enum(userRoomVisibilityValues),
  category: z.enum(liveRoomCategoryValues),
  hours: userRoomHoursSchema,
  where: userRoomWhereSchema,
  /** Checks the creator in open right away; left out they start as friends-only. */
  open: z.boolean().optional(),
})
export type UserRoomCreateInput = z.infer<typeof userRoomCreateSchema>

/** The new room: its event id, the check-in link behind the QR, and the short share code. */
export const userRoomCreateResponseSchema = z.object({
  eventId: z.string().uuid(),
  checkinLink: z.string(),
  shareCode: z.string(),
})
export type UserRoomCreateResponse = z.infer<typeof userRoomCreateResponseSchema>

/** Only widens visibility (private → friends → request → public); extending adds four hours. */
export const userRoomUpdateSchema = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  visibility: z.enum(userRoomVisibilityValues).optional(),
  extendHours: z.literal(USER_ROOM_EXTEND_HOURS).optional(),
})
export type UserRoomUpdateInput = z.infer<typeof userRoomUpdateSchema>

/** The user-room facts of a Live Room; `null` on an organizer's event. */
export const liveRoomUserRoomSchema = z.object({
  ownerId: z.string().uuid().nullable(),
  /** "Live Room von <hostFirstName>". */
  hostFirstName: z.string(),
  visibility: z.enum(userRoomVisibilityValues),
  category: z.enum(liveRoomCategoryValues),
  endsAt: z.string(),
  closedAt: z.string().nullable(),
  /** The room's cover photo ("Titelbild"), set by the host. */
  coverUrl: z.string().nullable(),
})
export type LiveRoomUserRoom = z.infer<typeof liveRoomUserRoomSchema>

export const userRoomResponseSchema = z.object({
  eventId: z.string().uuid(),
  title: z.string(),
  userRoom: liveRoomUserRoomSchema,
})
export type UserRoomResponse = z.infer<typeof userRoomResponseSchema>

export const userRoomRemoveSchema = z.object({ userId: z.string().uuid() })
export type UserRoomRemoveInput = z.infer<typeof userRoomRemoveSchema>

export const userRoomNearbyQuerySchema = z.object({
  lat: z.coerce.number().min(-90).max(90),
  lng: z.coerce.number().min(-180).max(180),
})

/** Rooms running within about 100 m the viewer may see: organizer events first, then the busiest. */
export const userRoomNearbyResponseSchema = z.object({
  rooms: z.array(
    z.object({
      eventId: z.string().uuid(),
      kind: z.enum(['event', 'room']),
      title: z.string(),
      here: z.number().int().nonnegative(),
      distanceM: z.number().nonnegative(),
    }),
  ),
})
export type UserRoomNearbyResponse = z.infer<typeof userRoomNearbyResponseSchema>

/** A map box, optionally narrowed to some kinds of rooms (comma list, e.g. `party,sport`). */
export const liveMapQuerySchema = z.object({
  /** `true`: only events for students. */
  studentsOnly: z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => value === 'true'),
  minLat: z.coerce.number().min(-90).max(90),
  minLng: z.coerce.number().min(-180).max(180),
  maxLat: z.coerce.number().min(-90).max(90),
  maxLng: z.coerce.number().min(-180).max(180),
  categories: z
    .string()
    .optional()
    .transform((value) =>
      value
        ? value
            .split(',')
            .map((item) => item.trim())
            .filter((item): item is LiveRoomCategory =>
              (liveRoomCategoryValues as readonly string[]).includes(item),
            )
        : undefined,
    ),
})

/** A map box for the places layer; `openNow=true` keeps only places open right now. */
export const liveMapPlacesQuerySchema = z.object({
  minLat: z.coerce.number().min(-90).max(90),
  minLng: z.coerce.number().min(-180).max(180),
  maxLat: z.coerce.number().min(-90).max(90),
  maxLng: z.coerce.number().min(-180).max(180),
  openNow: z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => value === 'true'),
})
export type LiveMapPlacesQuery = z.infer<typeof liveMapPlacesQuerySchema>

/**
 * Published places inside a map box, most popular first. `category` is the name of the place's
 * first tag; `isOpenNow` is null when the place has no opening hours.
 */
export const liveMapPlacesResponseSchema = z.object({
  places: z.array(
    z.object({
      listingId: z.string().uuid(),
      name: z.string(),
      lat: z.number(),
      lng: z.number(),
      category: z.string().nullable(),
      coverUrl: z.string().nullable(),
      isOpenNow: z.boolean().nullable(),
    }),
  ),
})
export type LiveMapPlacesResponse = z.infer<typeof liveMapPlacesResponseSchema>
